'use client';

import React, { useState } from 'react';
import { sendLeadEmail } from '@/app/actions/send-lead-email';
import {
  DATA,
  PHASES,
  TEAM_TIME,
  branchOption,
  buildChecklist,
  formatHkd,
  readinessScore,
  teamTime,
  timelineBand,
  type Answers,
  type Branch,
  type ChecklistExtras,
  type Score,
} from './model';
import { STR } from './strings';

const PHASE_COLORS = ['#0d555c', '#0f9993', '#a3e8e0', '#B08A2E', '#04445c'];
const LANE_MAX_WEEKS = 12;

/* ------------------------------ Lead form ------------------------------ */
type SendState = 'idle' | 'sending' | 'sent' | 'error';

function LeadForm({ checklist, intro, onSent }: { checklist: string; intro?: string; onSent?: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [state, setState] = useState<SendState>('idle');
  const [note, setNote] = useState('');
  const uid = 'cxg-lead';

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (state === 'sending') return;
    const n = name.trim();
    const em = email.trim();
    if (!n || !em) {
      setState('error');
      setNote(STR.lead.required);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(em)) {
      setState('error');
      setNote(STR.lead.invalidEmail);
      return;
    }
    setState('sending');
    setNote('');
    try {
      const res = await sendLeadEmail({
        leadLabel: STR.lead.leadLabel,
        name: n,
        email: em,
        company: company.trim() || undefined,
        message: checklist,
        page: 'netsuite-pricing',
        source: 'complexity-game',
        submittedAt: new Date().toISOString(),
      });
      if (res && res.success) {
        setState('sent');
        setNote(STR.lead.success);
        onSent?.();
      } else {
        setState('error');
        setNote(STR.lead.failure);
      }
    } catch {
      setState('error');
      setNote(STR.lead.failure);
    }
  };

  return (
    <div className="lead">
      <h4>{STR.lead.title}</h4>
      <p>{intro ?? STR.lead.body}</p>
      <form className="lead-form" onSubmit={submit} noValidate>
        <div className="field">
          <label htmlFor={`${uid}-name`}>{STR.lead.name}</label>
          <input id={`${uid}-name`} name="name" type="text" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} disabled={state === 'sent'} required />
        </div>
        <div className="field">
          <label htmlFor={`${uid}-email`}>{STR.lead.email}</label>
          <input id={`${uid}-email`} name="email" type="email" autoComplete="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={state === 'sent'} required />
        </div>
        <div className="field">
          <label htmlFor={`${uid}-company`}>{STR.lead.company}</label>
          <input id={`${uid}-company`} name="company" type="text" autoComplete="organization" value={company} onChange={(e) => setCompany(e.target.value)} disabled={state === 'sent'} />
        </div>
        <div className="actions" style={{ gridColumn: '1 / -1' }}>
          <button type="submit" className="btn btn-primary" disabled={state === 'sending' || state === 'sent'}>
            {state === 'sending' ? STR.lead.sending : STR.lead.submit}
          </button>
          <a className="alt" href={STR.lead.bookCallHref}>
            {STR.lead.bookCall}
          </a>
        </div>
      </form>
      <p className={`status${state === 'sent' ? ' ok' : state === 'error' ? ' err' : ''}`} role="status" aria-live="polite">
        {note}
      </p>
    </div>
  );
}

/* ------------------------------ Branches ------------------------------ */
type Track = (event: string, props: Record<string, string | number | boolean>) => void;

/** Short end screen for "already on NetSuite" and "project in trouble". Neither plays the game. */
export function BranchScreen({ branch, answers, onBack, onPlay, onCta }: { branch: Exclude<Branch, 'choosing'>; answers: Answers; onBack: () => void; onPlay: () => void; onCta: (href: string) => void }) {
  const copy = STR.branch[branch];
  const href =
    branchOption(answers)?.cta_url ??
    (branch === 'rescue' ? DATA.result.rescue_result.cta_url : DATA.result.managed_services_result.cta_url);
  return (
    <div className="res">
      <p className="res-kicker">{copy.kicker}</p>
      <div className="res-level">
        <span className="res-level-name">{copy.title}</span>
      </div>
      <p className="res-lead">{copy.body}</p>
      {branch === 'rescue' && (
        <p className="band-basis">
          {STR.result.timelineBasis}
          <a href={DATA.result.rescue_result.source} target="_blank" rel="noopener noreferrer">{STR.branch.rescue.source}</a>
        </p>
      )}
      <div className="branch-cta">
        <a className="btn btn-primary" href={href} onClick={() => onCta(href)}>{copy.cta}</a>
      </div>
      <div className="res-actions">
        <button type="button" className="btn btn-ghost" onClick={onBack}>{STR.branch.back}</button>
        <button type="button" className="btn-link" onClick={onPlay}>{STR.branch.playAnyway}</button>
      </div>
    </div>
  );
}

/* ---------------------------- Not included ---------------------------- */
function NotInEstimate({ level }: { level: number }) {
  const tt = teamTime(level);
  const sal = TEAM_TIME.salaries_hkd_per_month;
  return (
    <div className="excl">
      <h5 className="excl-h">
        {STR.notInEstimate.title}
      </h5>
      <ul className="excl-list">
        {DATA.result.not_in_estimate.map((id) => {
          const row = STR.notInEstimate.rows[id];
          if (!row) return null;
          if (id === 'team_time') {
            return (
              <li className="excl-row" key={id}>
                <b>{row.title}</b>
                <span className="amt">{STR.teamTime.about(formatHkd(tt.hkd))}</span>
                {STR.teamTime.assumption(tt.accountant_share, tt.finance_manager_share, tt.months)} {STR.teamTime.salaryOnly}
                <span className="by">
                  {STR.teamTime.source(formatHkd(sal.accountant.median), formatHkd(sal.finance_manager.median))}{' '}
                  <a href={sal.accountant.url} target="_blank" rel="noopener noreferrer">{STR.teamTime.accountantLink}</a>
                  {', '}
                  <a href={sal.finance_manager.url} target="_blank" rel="noopener noreferrer">{STR.teamTime.managerLink}</a>
                  {'. '}
                  {STR.teamTime.provisional} {STR.teamTime.checked}
                </span>
              </li>
            );
          }
          return (
            <li className="excl-row" key={id}>
              <b>{row.title}</b>
              <br />
              {row.body}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ReadinessBlock({ answers }: { answers: Answers }) {
  const r = readinessScore(answers);
  return (
    <section className="res-section">
      <h4 className="res-h">{STR.readiness.title}</h4>
      {r.score === null || !r.tier ? (
        <p className="res-sub">{STR.readiness.skipped}</p>
      ) : (
        <>
          <div className="ready">
            <span className="ready-num">{r.score}<small>{STR.readiness.outOf}</small></span>
            <span className="ready-tier">{STR.readiness.tiers[r.tier]}</span>
          </div>
          <p className="ready-next">{STR.readiness.next[r.weakest ?? 'all_good']}</p>
          <p className="res-sub">
            {r.rated < r.total ? `${STR.readiness.partial(r.rated, r.total)} ` : ''}
            {STR.readiness.basis}
          </p>
        </>
      )}
    </section>
  );
}

/* ------------------------------ Result -------------------------------- */
export function ResultScreen({ answers, score, extras, onRestart, onBack, onTrack }: { answers: Answers; score: Score; extras?: ChecklistExtras; onRestart: () => void; onBack: () => void; onTrack: Track }) {
  const { level } = score;
  const checklist = buildChecklist(answers, score, extras);
  const phaseTotal = PHASES.reduce((s, p) => s + (p[1] + p[2]) / 2, 0);

  return (
    <div className="res">
      <p className="res-kicker">{STR.result.kicker}</p>
      <div className="res-level" aria-label={STR.result.levelLabel(level.level)}>
        <span className="res-level-num">{level.level}<small>{STR.result.ofFive}</small></span>
        <span className="res-level-name">{level.name}</span>
      </div>
      <p className="res-lead">{STR.result.scopeNote}</p>

      <section className="res-section">
        <h4 className="res-h">{STR.result.driversTitle}</h4>
        {score.top.length === 0 ? (
          <p className="res-sub">{STR.result.driversNone}</p>
        ) : (
          <ol className="drivers">
            {score.top.map((c) => (
              <li className="driver" key={c.id}>
                <span>{c.label}</span>
                <span className="driver-pts">{STR.result.driverPoints(c.points)}</span>
              </li>
            ))}
          </ol>
        )}
      </section>

      <section className="res-section">
        <h4 className="res-h">{STR.result.timelineTitle}</h4>
        <p className="band">{timelineBand(level)}</p>
        <p className="band-basis">{STR.result.timelineBasis}{level.timeline_basis}</p>
      </section>

      <section className="res-section">
        <h4 className="res-h">{STR.result.integrationsTitle}</h4>
        <p className="res-sub">{score.integrations.length ? STR.result.integrationsNote : STR.result.integrationsNone}</p>
        {score.integrations.length > 0 && (
          <ul className="lanes">
            {score.integrations.map((i) => (
              <li className="lane" key={i.slug}>
                <span className="lane-name">{i.name}</span>
                <span className="lane-weeks">{STR.integrations.weeks(i.weeks_min, i.weeks_max)}</span>
                <span className="lane-bar" aria-hidden="true">
                  <span style={{ left: `${(i.weeks_min / LANE_MAX_WEEKS) * 100}%`, width: `${((i.weeks_max - i.weeks_min) / LANE_MAX_WEEKS) * 100}%` }} />
                </span>
                <a className="lane-link" href={i.live_url} target="_blank" rel="noopener noreferrer">{STR.result.integrationLink}</a>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="res-section">
        <h4 className="res-h">{STR.result.costTitle}</h4>
        {score.costGroups.length === 0 ? (
          <p className="res-sub">{STR.result.costNone}</p>
        ) : (
          <div className="cost-groups">
            {score.costGroups.map((g) => (
              <div className="cost-group" key={g.stepId}>
                <h5>{g.title}</h5>
                <ul>{g.lines.map((l) => <li key={l}>{l}</li>)}</ul>
              </div>
            ))}
          </div>
        )}
        <NotInEstimate level={level.level} />
      </section>

      <ReadinessBlock answers={answers} />

      <section className="res-section">
        <h4 className="res-h">{STR.result.phaseTitle}</h4>
        <p className="res-sub">{STR.result.phaseSub}</p>
        <div className="phase-bar" role="img" aria-label={PHASES.map((p) => `${p[0]} ${STR.result.phasePct(p[1], p[2])}`).join(', ')}>
          {PHASES.map((p, i) => (
            <div className="phase-seg" key={p[0]} style={{ width: `${(((p[1] + p[2]) / 2) / phaseTotal) * 100}%`, background: PHASE_COLORS[i % PHASE_COLORS.length] }} />
          ))}
        </div>
        <ul className="phase-legend">
          {PHASES.map((p, i) => (
            <li key={p[0]}>
              <span className="swatch" style={{ background: PHASE_COLORS[i % PHASE_COLORS.length] }} />
              <span>{p[0]}</span>
              <span className="pct">{STR.result.phasePct(p[1], p[2])}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="res-section">
        <h4 className="res-h">{STR.result.feeTitle}</h4>
        <div className="fee-box" role="note">{STR.result.feePlaceholder}</div>
        <p className="fee-note">{STR.result.feeNote}</p>
      </section>

      <section className="res-section">
        <h4 className="res-h">{STR.result.afterTitle}</h4>
        <p className="after">
          {STR.result.afterBody}
          <span className="by">{STR.result.afterSource}</span>
        </p>
      </section>

      <LeadForm checklist={checklist} onSent={() => onTrack('checklist_sent', { level: level.level })} />
      <p className="second-op">
        <a href={STR.result.secondOpinionHref} onClick={() => onTrack('second_opinion', { level: level.level })}>
          {STR.result.secondOpinion}
        </a>
      </p>

      <div className="res-actions">
        <button type="button" className="btn btn-ghost" onClick={onBack}>{STR.back}</button>
        <button type="button" className="btn-link" onClick={onRestart}>{STR.startAgain}</button>
      </div>
    </div>
  );
}
